import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Sun, X, Send } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_SESSION = "bl_chat_session";

// Chatbot UI copy (multi-language, kept inline). "Surya" (sun) is the bot's name — kept as-is across languages.
const UI = {
  en: { title: "Surya", subtitle: "Your Solar Buddy", placeholder: "Type your reply…", askLabel: "Chat with Surya", online: "Online", thanks: "Thanks — details saved!" },
  hi: { title: "सूर्य", subtitle: "आपका सोलर साथी", placeholder: "अपना जवाब लिखें…", askLabel: "सूर्य से बात करें", online: "ऑनलाइन", thanks: "धन्यवाद — विवरण सेव हो गया!" },
  mr: { title: "सूर्य", subtitle: "तुमचा सोलर मित्र", placeholder: "तुमचं उत्तर टाइप करा…", askLabel: "सूर्यशी बोला", online: "ऑनलाइन", thanks: "धन्यवाद — तपशील सेव्ह!" },
  ta: { title: "சூர்யா", subtitle: "உங்கள் சோலார் துணை", placeholder: "உங்கள் பதிலை தட்டச்சு செய்யுங்கள்…", askLabel: "சூர்யாவுடன் பேசுங்கள்", online: "ஆன்லைன்", thanks: "நன்றி — விவரங்கள் சேமிக்கப்பட்டன!" },
  te: { title: "సూర్య", subtitle: "మీ సోలార్ మిత్రుడు", placeholder: "మీ సమాధానం టైప్ చేయండి…", askLabel: "సూర్యతో చాట్ చేయండి", online: "ఆన్‌లైన్", thanks: "ధన్యవాదాలు — వివరాలు సేవ్!" },
  bn: { title: "সূর্য", subtitle: "আপনার সোলার বন্ধু", placeholder: "আপনার উত্তর লিখুন…", askLabel: "সূর্যের সাথে চ্যাট করুন", online: "অনলাইন", thanks: "ধন্যবাদ — বিবরণ সেভ!" },
  kn: { title: "ಸೂರ್ಯ", subtitle: "ನಿಮ್ಮ ಸೋಲಾರ್ ಸ್ನೇಹಿತ", placeholder: "ನಿಮ್ಮ ಉತ್ತರ ಟೈಪ್ ಮಾಡಿ…", askLabel: "ಸೂರ್ಯನೊಂದಿಗೆ ಚಾಟ್ ಮಾಡಿ", online: "ಆನ್‌ಲೈನ್", thanks: "ಧನ್ಯವಾದ — ವಿವರಗಳು ಸೇವ್!" },
  gu: { title: "સૂર્ય", subtitle: "તમારો સોલાર સાથી", placeholder: "તમારો જવાબ લખો…", askLabel: "સૂર્ય સાથે ચેટ કરો", online: "ઓનલાઇન", thanks: "આભાર — વિગતો સેવ!" },
};

// Render simple **bold** markdown-ish text into JSX safely (no external lib)
function renderRich(text) {
  if (!text) return null;
  // Split by **bold** first, then by *italic*, then by newline
  const parts = [];
  const lines = text.split(/\n/);
  lines.forEach((line, li) => {
    let idx = 0;
    const boldRe = /\*\*([^*]+)\*\*/g;
    let m;
    while ((m = boldRe.exec(line)) !== null) {
      if (m.index > idx) parts.push({ t: "t", v: line.slice(idx, m.index) });
      parts.push({ t: "b", v: m[1] });
      idx = m.index + m[0].length;
    }
    if (idx < line.length) parts.push({ t: "t", v: line.slice(idx) });
    if (li < lines.length - 1) parts.push({ t: "br" });
  });
  // Handle *italic* inside plain text
  const finalNodes = [];
  parts.forEach((p, i) => {
    if (p.t === "b") {
      finalNodes.push(<strong key={i} className="font-semibold">{p.v}</strong>);
    } else if (p.t === "br") {
      finalNodes.push(<br key={i} />);
    } else {
      const italicRe = /\*([^*]+)\*/g;
      const chunks = [];
      let idx = 0;
      let mi;
      while ((mi = italicRe.exec(p.v)) !== null) {
        if (mi.index > idx) chunks.push({ t: "t", v: p.v.slice(idx, mi.index) });
        chunks.push({ t: "i", v: mi[1] });
        idx = mi.index + mi[0].length;
      }
      if (idx < p.v.length) chunks.push({ t: "t", v: p.v.slice(idx) });
      chunks.forEach((c, ci) => {
        if (c.t === "i") finalNodes.push(<em key={`${i}-${ci}`} className="italic opacity-90">{c.v}</em>);
        else finalNodes.push(<span key={`${i}-${ci}`}>{c.v}</span>);
      });
    }
  });
  return finalNodes;
}

export default function Chatbot() {
  const { lang } = useTranslation();
  const ui = UI[lang] || UI.en;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(LS_SESSION) || null; } catch (_e) { return null; }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [done, setDone] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, quickReplies]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 200);
    }
  }, [open]);

  const openChat = useCallback(async () => {
    setOpen(true);
    if (messages.length === 0 && !opening) {
      setOpening(true);
      try {
        const { data } = await axios.post(`${API}/chat/open`, {
          session_id: sessionId,
          language: lang,
        });
        if (data.session_id) {
          setSessionId(data.session_id);
          try { localStorage.setItem(LS_SESSION, data.session_id); } catch (_e) { /* ignore */ }
        }
        if (data.reply) setMessages([{ role: "assistant", content: data.reply }]);
        setQuickReplies(data.quick_replies || []);
        setDone(!!data.done);
      } catch (_err) {
        setMessages([{
          role: "assistant",
          content: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        }]);
      } finally {
        setOpening(false);
      }
    }
  }, [messages.length, sessionId, lang, opening]);

  const closeChat = useCallback(() => {
    setOpen(false);
    if (sessionId) {
      try {
        const url = `${API}/chat/close`;
        const payload = JSON.stringify({ session_id: sessionId, language: lang });
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: "application/json" });
          navigator.sendBeacon(url, blob);
        } else {
          axios.post(url, { session_id: sessionId, language: lang }).catch(() => {});
        }
      } catch (_e) { /* ignore */ }
    }
  }, [sessionId, lang]);

  const submitText = useCallback(async (text) => {
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setQuickReplies([]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        language: lang,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(LS_SESSION, data.session_id); } catch (_e) { /* ignore */ }
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "…" }]);
      setQuickReplies(data.quick_replies || []);
      setDone(!!data.done);
      if (data.lead_captured) setLeadDone(true);
    } catch (_err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Sorry, I couldn't reach the server. Please try again in a moment.",
      }]);
    } finally {
      setSending(false);
    }
  }, [sending, sessionId, lang]);

  const sendMessage = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    submitText(text);
  }, [input, submitText]);

  const sendQuickReply = useCallback((qr) => {
    if (sending) return;
    submitText(qr.label);  // send the localized label as the user's text; backend maps via regex
  }, [sending, submitText]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={openChat}
          data-testid="chatbot-open-btn"
          aria-label={ui.askLabel}
          className="fixed z-40 bottom-5 right-5 group flex items-center gap-2.5 pl-2 pr-5 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-full shadow-lg hover:shadow-[0_16px_32px_-8px_rgba(22,163,74,0.55)] hover:-translate-y-0.5 transition-all"
        >
          <span className="relative flex items-center justify-center w-9 h-9 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
            <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500 flex items-center justify-center shadow-inner">
              <Sun className="w-5 h-5 text-white" strokeWidth={2.4} />
            </span>
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-sm font-bold whitespace-nowrap">{ui.askLabel}</span>
            <span className="text-[10px] font-medium opacity-80 whitespace-nowrap">{ui.subtitle}</span>
          </span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed z-50 bottom-5 right-5 w-[92vw] max-w-[380px] h-[75vh] max-h-[600px] flex flex-col bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden animate-in fade-in slide-in-from-bottom-5"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-700 to-green-600 text-white">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-inner">
              <Sun className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] leading-tight truncate">{ui.title}</div>
              <div className="text-[11px] opacity-90 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                {ui.subtitle}
              </div>
            </div>
            <button
              type="button"
              onClick={closeChat}
              data-testid="chatbot-close-btn"
              aria-label="Close chat"
              className="p-1.5 rounded-full hover:bg-white/15 transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={2.2} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-stone-50"
          >
            {opening && messages.length === 0 && <TypingBubble />}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {sending && <TypingBubble />}
            {leadDone && (
              <div className="text-center text-xs text-green-700 font-semibold pt-1" data-testid="chatbot-lead-toast">
                ✓ {ui.thanks}
              </div>
            )}
          </div>

          {/* Quick replies */}
          {quickReplies && quickReplies.length > 0 && !sending && (
            <div className="px-3 pb-2 flex flex-wrap gap-2 bg-stone-50" data-testid="chatbot-quick-replies">
              {quickReplies.map((qr, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => sendQuickReply(qr)}
                  disabled={sending}
                  data-testid={`chatbot-qr-${qr.value}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border-2 border-green-600 text-green-700 bg-white hover:bg-green-50 hover:shadow-sm transition-all disabled:opacity-50"
                >
                  {qr.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={sendMessage}
            className="flex items-end gap-2 p-3 border-t border-stone-200 bg-white"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={done ? "…" : ui.placeholder}
              data-testid="chatbot-input"
              disabled={sending || opening || done}
              className="flex-1 resize-none rounded-xl border border-stone-300 focus:border-green-600 focus:ring-2 focus:ring-green-100 outline-none px-3 py-2 text-sm max-h-24 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={sending || opening || done || !input.trim()}
              data-testid="chatbot-send-btn"
              aria-label="Send"
              className="shrink-0 w-10 h-10 rounded-full bg-green-600 hover:bg-green-700 disabled:bg-stone-300 text-white flex items-center justify-center transition-colors"
            >
              <Send className="w-4 h-4" strokeWidth={2.4} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={isUser ? "chatbot-msg-user" : "chatbot-msg-bot"}
    >
      <div
        className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words rounded-2xl shadow-sm ${
          isUser
            ? "bg-green-600 text-white rounded-br-md"
            : "bg-white text-stone-800 border border-stone-200 rounded-bl-md"
        }`}
      >
        {isUser ? content : renderRich(content)}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start" data-testid="chatbot-typing">
      <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-bounce" style={{ animationDelay: "240ms" }} />
        </div>
      </div>
    </div>
  );
}
